// TrackVideoPage — orientation video shown right after signup.
// On "I've watched" the user is marked + routed to form (or quiz for after-10th).

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, PlayCircle, ArrowRight, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import { useAuth } from '../lib/auth';
import { fetchMyTrack, markVideoWatched } from '../lib/tracksApi';

export default function TrackVideoPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [trackData, setTrackData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user === null) return;
    if (user === false) { navigate('/signin', { replace: true }); return; }
    if (user.is_admin) { navigate('/dashboard', { replace: true }); return; }
    (async () => {
      try {
        const data = await fetchMyTrack();
        if (!data.selected_track) {
          navigate('/onboarding/track', { replace: true });
          return;
        }
        // If video already watched, skip ahead
        if (data.track_video_watched) {
          if (data.track?.post_signup === 'quiz') {
            navigate('/onboarding/quiz', { replace: true });
          } else {
            navigate('/onboarding/form', { replace: true });
          }
          return;
        }
        setTrackData(data);
      } catch {
        setError('Could not load track. Please refresh.');
      } finally {
        setLoading(false);
      }
    })();
  }, [user, navigate]);

  const handleContinue = async () => {
    setMarking(true);
    setError('');
    try {
      await markVideoWatched();
      await refresh();
      if (trackData.track?.post_signup === 'quiz') {
        navigate('/onboarding/quiz', { replace: true });
      } else {
        navigate('/onboarding/form', { replace: true });
      }
    } catch {
      setError('Could not save progress. Please try again.');
      setMarking(false);
    }
  };

  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-blue-50">
        <Loader2 className="w-10 h-10 animate-spin text-teal-600" />
      </div>
    );
  }
  if (!trackData) return null;
  const { track } = trackData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50">
      <Navbar />
      <div className="pt-28 pb-16 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-medium mb-4">
              <Lock size={12}/> Step 1 of 2 · Watch to unlock
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              {track?.name}
            </h1>
            <p className="text-gray-600">{track?.video_title || 'Quick orientation — 3 minutes.'}</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-3xl shadow-xl overflow-hidden mb-6" data-testid="track-video-wrapper">
            <div className="aspect-video bg-gray-100 relative">
              {track?.video_url ? (
                <iframe
                  src={track.video_url}
                  title={track.video_title || 'Track orientation'}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                  data-testid="track-video-iframe"
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
                  <PlayCircle className="w-16 h-16 mb-2" />
                  <p className="text-sm">Video coming soon — you can continue below.</p>
                </div>
              )}
            </div>
            <div className="p-6 bg-gradient-to-r from-amber-50 to-orange-50 border-t border-amber-200">
              <p className="text-sm text-gray-700">
                <strong>Heads up:</strong> after this video, you'll see ONE short interest form. Your answers
                unlock <strong>3–5 careers handpicked for you</strong>. Skim the video carefully — it shapes everything ahead.
              </p>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 mb-4">{error}</div>
          )}

          <div className="flex justify-center">
            <Button
              size="lg"
              onClick={handleContinue}
              disabled={marking}
              className="btn-primary"
              data-testid="track-video-continue-btn"
            >
              {marking ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : null}
              I've watched — continue <ArrowRight className="w-4 h-4 ml-2"/>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
